import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UserData } from "@/entities/users/types";
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/utils/utils";
import ScoringTableCardController, {
  ScoringTableCardControllerProps
} from "@/components/layouts/ScoringTableCard/ScoringTableCardController";
import HorizBarPercentage from "@/components/controls/HorizBarPercentage";
import Text from "@/components/ui/text";
import { OverallScoringData, SettingsPeriod } from "@/entities/reporting/types";
import { StakeholdersMappingProvider } from "./StakeholdersMappingContext";
import { useThresholds } from "@/entities/settings/hooks";
import { SettingsThresholdsType } from "@/entities/settings/types";
import { numberSortingFn } from "@/utils/numbers";

interface Props {
  leadUser: UserData | undefined;
  overallData: OverallScoringData;
  period: SettingsPeriod;
  isLoading?: boolean;
}

const StakeholdersMapping: React.FC<Props> = ({
  leadUser,
  overallData,
  period,
  isLoading
}) => {
  const { t } = useTranslation();

  const { thresholds } = useThresholds();
  const stakeholdersThresholds = useMemo(
    () => thresholds.find((th) => th.id === SettingsThresholdsType.KEY_STAKEHOLDERS_MAPPING_SCORE),
    [thresholds]
  );

  const overallTrendUp = useMemo(
    () =>
      overallData?.trend.scoreForPeriod > overallData?.trend.comparedToScore,
    [overallData]
  );
  const stakeholdersProps: ScoringTableCardControllerProps = useMemo(
    () => ({
      dataTestPrefix: "stakeholders",
      labelProps: {
        title: t("stakeholders.title"),
        subtitle: t("stakeholders.subtitle"),
        empty: {
          title: t("stakeholders.empty.title"),
          subtitle: t("stakeholders.empty.subtitle")
        }
      },
      headerDataWidget: (
        <div
          data-test="stakeholders-overall-score"
          className="min-w-28 w-28 min-h-20 h-20 flex flex-col flex-nowrap justify-start content-stretch align-start mx-1 my-2"
        >
          <Text as="div" variant="smBodySecondary" className="w-28 mx-2 mb-0.5">
            {t("stakeholders.overall.title")}
          </Text>
          <HorizBarPercentage
            value={overallData?.averageScore ?? 0}
            className="text-lg text-primary font-normal flex-row align-center justify-end mx-2"
            levels={stakeholdersThresholds}
          />
          <Text
            as="div"
            variant="smBodySecondary"
            className="flex flex-row w-28"
          >
            <span
              className={cn(
                "flex items-end justify-end size-5 mx-1",
                overallTrendUp ? "text-charts-positive" : "text-charts-negative"
              )}
            >
              {overallTrendUp ? <ArrowUp /> : <ArrowDown />}
            </span>
            <span className="self-center">
              {t("stakeholders.overall.compared")}
            </span>
          </Text>
        </div>
      ),
      tableDef: {
        simpleColumns: [
          { name: t("stakeholders.table.name") },
          { name: t("stakeholders.table.directReporters") },
          { name: t("stakeholders.table.weakopportunities") }
        ],
        summaryColumn: {
          header: t("stakeholders.table.avgscore"),
          accessorKey: "summaryValue",
          size: 150,
          cell: ({ getValue }: { getValue: () => number }) => (
            <HorizBarPercentage
              value={getValue()}
              className="min-w-36 h-6"
              levels={stakeholdersThresholds}
            />
          ),
          sortingFn: numberSortingFn,
          meta: {
            textAlign: "start"
          }
        },
        initialSummarySortDesc: false
      },
      isLoading
    }),
    [t, overallData, overallTrendUp, stakeholdersThresholds]
  );

  return (
    <StakeholdersMappingProvider leadUser={leadUser} period={period}>
      <ScoringTableCardController {...stakeholdersProps} />
    </StakeholdersMappingProvider>
  );
};

export default StakeholdersMapping;
